/**
 * The calibre family's binding of {@link TierVocabularySection}:
 * `calibre_families/{id}.tierVocabulary` — additions to the manufacturer's
 * list for this family's calibres. Generic grade names only: brand-specific
 * tiers live on the calibre (James, 2026-10-29), and the validator refuses a
 * brand-scoped entry here.
 *
 * Adds the promote prompt (memo "Promote prompt", D16/D17/D23): adding a name
 * that looks manufacturer-wide asks whether it belongs on the manufacturer
 * instead — as a synonym of an existing grade, or as a new manufacturer tier —
 * and after a promotion offers to remove copies from other families, one tick
 * each. Without evidence the name is added here and a quiet "Promote…" link
 * stays on the row. Suffix codes (-1, SQ, CSL) never get the prompt.
 *
 * Family overrides of a manufacturer grade (memo P4) are not modelled yet:
 * shared-types has no `overridesManufacturerTierId`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type { TierVocabularyEntry } from '@watchkeeper/shared-types';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { TierVocabularySection, type TierVocabularyOwner } from '../TierVocabulary/TierVocabularySection';
import {
  getManufacturerTierNamesContext,
  mutateFamilyTierVocabulary,
  mutateManufacturerTierVocabulary,
  onTierVocabularyChanged,
} from '../../services/firebase';
import {
  addAkasToTierEntry,
  familyCopiesOf,
  isGenericTierEntry,
  looksLikeTierSuffixCode,
  newTierEntryId,
  promoteEvidence,
  removeTierEntry,
  sortTierVocabulary,
  upsertTierEntry,
  type FamilyTierNamesSource,
  type PromoteEvidence,
} from '../../utils/tierVocabulary';

interface Props {
  manufacturerId: string;
  family: FamilyTierNamesSource;
  /** Every family of this manufacturer (this one included is fine — it is skipped). */
  families: FamilyTierNamesSource[];
}

const NONE: TierVocabularyEntry[] = [];

type PromoteChoice = 'synonym' | 'new' | 'family';

interface PromoteState {
  entry: TierVocabularyEntry;
  evidence: PromoteEvidence | null;
  /** 'add' = intercepting a new family entry; 'row' = promoting one already stored here. */
  source: 'add' | 'row';
  resolve?: (outcome: 'write' | 'handled' | 'cancel') => void;
}

interface CopiesState {
  name: string;
  rows: Array<{ family: FamilyTierNamesSource; copies: TierVocabularyEntry[] }>;
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function FamilyTierNamesSection({ manufacturerId, family, families }: Props) {
  const [mfr, setMfr] = useState<{ name: string; entries: TierVocabularyEntry[] } | null>(null);
  const [siblings, setSiblings] = useState<FamilyTierNamesSource[]>(() => families.filter((f) => f.id !== family.id));
  const [promote, setPromote] = useState<PromoteState | null>(null);
  const [copies, setCopies] = useState<CopiesState | null>(null);

  useEffect(() => setSiblings(families.filter((f) => f.id !== family.id)), [families, family.id]);

  useEffect(() => {
    let cancelled = false;
    getManufacturerTierNamesContext(manufacturerId)
      .then((ctx) => !cancelled && setMfr(ctx))
      .catch((err) => {
        console.warn('Family tier names: manufacturer read failed', err);
        if (!cancelled) setMfr({ name: manufacturerId, entries: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [manufacturerId]);

  // Keep the manufacturer list and the sibling families current as this tab writes them.
  useEffect(
    () =>
      onTierVocabularyChanged((kind, id, next) => {
        if (kind === 'manufacturer' && id === manufacturerId) setMfr((m) => (m ? { ...m, entries: next } : m));
        if (kind === 'family') setSiblings((prev) => prev.map((f) => (f.id === id ? { ...f, tierVocabulary: next } : f)));
      }),
    [manufacturerId],
  );

  const mfrName = mfr?.name ?? 'the manufacturer';
  const mfrEntries = mfr?.entries ?? NONE;
  // Latest values for the async intercept, without re-creating the owner.
  const latest = useRef({ mfrEntries, siblings });
  latest.current = { mfrEntries, siblings };

  const owner: TierVocabularyOwner = useMemo(
    () => ({
      kind: 'family',
      id: family.id,
      name: `${family.name} family`,
      entries: family.tierVocabulary ?? NONE,
      reserved: mfrEntries,
      mutate: (change) => mutateFamilyTierVocabulary(family.id, manufacturerId, change),
      interceptAdd: (entry) => {
        const evidence = promoteEvidence(entry, latest.current.mfrEntries, latest.current.siblings);
        if (!evidence) return Promise.resolve('write');
        return new Promise((resolve) => setPromote({ entry, evidence, source: 'add', resolve }));
      },
    }),
    [family.id, family.name, family.tierVocabulary, manufacturerId, mfrEntries],
  );

  const offerCopyRemoval = useCallback((entry: TierVocabularyEntry) => {
    const rows = latest.current.siblings
      .map((f) => ({ family: f, copies: familyCopiesOf(f, entry.canonicalName, entry.brandId) }))
      .filter((r) => r.copies.length > 0);
    if (rows.length > 0) setCopies({ name: entry.canonicalName, rows });
  }, []);

  const runPromote = useCallback(
    async (state: PromoteState, choice: PromoteChoice, targetId: string) => {
      const { entry } = state;
      if (choice === 'family') {
        setPromote(null);
        state.resolve?.('write');
        return;
      }
      try {
        if (choice === 'synonym') {
          await mutateManufacturerTierVocabulary(manufacturerId, (cur) =>
            addAkasToTierEntry(cur, targetId, [entry.canonicalName, ...entry.akas]),
          );
        } else {
          // A brand tier stays a brand tier: promoting it makes a
          // manufacturer-level brand tier, never a generic grade.
          await mutateManufacturerTierVocabulary(manufacturerId, (cur) =>
            upsertTierEntry(cur, { ...entry, id: newTierEntryId() }),
          );
        }
        if (state.source === 'row') {
          await mutateFamilyTierVocabulary(family.id, manufacturerId, (cur) => removeTierEntry(cur, entry.id));
        }
        toast.success(
          choice === 'synonym'
            ? `“${entry.canonicalName}” added to ${mfrName} as another spelling.`
            : `“${entry.canonicalName}” is now a ${mfrName} tier name.`,
        );
        setPromote(null);
        state.resolve?.('handled');
        offerCopyRemoval(entry);
      } catch (err) {
        console.error('Promote tier name failed:', err);
        toast.error(`Could not promote: ${errorText(err)}`);
      }
    },
    [family.id, manufacturerId, mfrName, offerCopyRemoval],
  );

  const rowExtra = useCallback(
    (entry: TierVocabularyEntry) =>
      looksLikeTierSuffixCode(entry.canonicalName) ? null : (
        <button
          type="button"
          className="text-xs text-gray-500 hover:text-brand-600 hover:underline mr-2"
          onClick={() =>
            setPromote({
              entry,
              evidence: promoteEvidence(entry, latest.current.mfrEntries, latest.current.siblings),
              source: 'row',
            })
          }
          title={`Move this name to ${mfrName}'s tier names`}
        >
          Promote to {mfrName}…
        </button>
      ),
    [mfrName],
  );

  return (
    <>
      <TierVocabularySection
        owner={owner}
        rowExtra={rowExtra}
        intro={
          <>
            Names this family adds to {mfrName}&rsquo;s tier names, for its own calibres only — e.g. a family-only
            grade, or a brand&rsquo;s own modification (set the brand). Grade names {mfrName} uses everywhere belong
            on the manufacturer.
            {mfrEntries.length > 0 && (
              <span className="block mt-1">
                From {mfrName}: {sortTierVocabulary(mfrEntries).filter(isGenericTierEntry).map((e) => e.canonicalName).join(' · ') || '—'}
              </span>
            )}
          </>
        }
      />
      {promote && (
        <PromoteTierNameDialog
          state={promote}
          manufacturerName={mfrName}
          manufacturerEntries={mfrEntries}
          onCancel={() => {
            setPromote(null);
            promote.resolve?.('cancel');
          }}
          onChoose={(choice, targetId) => runPromote(promote, choice, targetId)}
        />
      )}
      {copies && (
        <RemoveCopiesDialog
          state={copies}
          onClose={() => setCopies(null)}
          onRemove={async (ticked) => {
            let failed = 0;
            for (const row of ticked) {
              const ids = new Set(row.copies.map((c) => c.id));
              try {
                await mutateFamilyTierVocabulary(row.family.id, manufacturerId, (cur) => cur.filter((e) => !ids.has(e.id)));
              } catch (err) {
                failed += 1;
                console.error('Remove family copy failed:', err);
              }
            }
            if (failed > 0) toast.error(`Could not update ${failed} famil${failed === 1 ? 'y' : 'ies'}.`);
            else toast.success(`Removed from ${ticked.length} famil${ticked.length === 1 ? 'y' : 'ies'}.`);
            setCopies(null);
          }}
        />
      )}
    </>
  );
}

function PromoteTierNameDialog({
  state,
  manufacturerName,
  manufacturerEntries,
  onCancel,
  onChoose,
}: {
  state: PromoteState;
  manufacturerName: string;
  manufacturerEntries: TierVocabularyEntry[];
  onCancel: () => void;
  onChoose: (choice: PromoteChoice, targetId: string) => Promise<void>;
}) {
  const { entry, evidence, source } = state;
  const grades = useMemo(() => sortTierVocabulary(manufacturerEntries).filter(isGenericTierEntry), [manufacturerEntries]);
  // A brand tier can never be a synonym of a generic grade.
  const synonymAllowed = !entry.brandId && grades.length > 0;
  const preselect: PromoteChoice =
    evidence?.nearMatch && synonymAllowed ? 'synonym' : evidence && evidence.otherFamilies.length > 0 ? 'new' : source === 'add' ? 'family' : 'new';
  const [choice, setChoice] = useState<PromoteChoice>(preselect);
  const [targetId, setTargetId] = useState(evidence?.nearMatch?.id ?? grades[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const target = grades.find((g) => g.id === targetId);

  return (
    <Modal isOpen onClose={onCancel} title="Promote tier name?" size="md" dismissOnBackdrop={false}>
      <div className="space-y-3 text-sm">
        <p>
          &ldquo;{entry.canonicalName}&rdquo;{' '}
          {evidence ? `looks like a ${manufacturerName}-wide tier name. Want to promote it?` : `— move it to ${manufacturerName}'s tier names?`}
          {entry.brandId && <span className="block text-xs text-gray-500 mt-1">It is a brand tier and stays one wherever it goes.</span>}
        </p>
        {evidence && (
          <ul className="text-xs text-gray-600 bg-gray-50 border rounded p-2 space-y-0.5">
            {evidence.nearMatch && (
              <li>
                {evidence.exactSynonym ? 'Already a spelling of' : 'Looks like'} {manufacturerName}&rsquo;s &ldquo;{evidence.nearMatch.canonicalName}&rdquo;.
              </li>
            )}
            {evidence.otherFamilies.length > 0 && <li>Also used in: {evidence.otherFamilies.map((f) => `${f.name} family`).join(', ')}.</li>}
          </ul>
        )}

        <label className={`flex gap-2 ${synonymAllowed ? '' : 'opacity-50'}`}>
          <input type="radio" name="promote" checked={choice === 'synonym'} disabled={!synonymAllowed} onChange={() => setChoice('synonym')} className="mt-1" />
          <span>
            {evidence?.exactSynonym && targetId === evidence.nearMatch?.id
              ? <>Use {manufacturerName}&rsquo;s &ldquo;{target?.canonicalName}&rdquo; — nothing new is added to this family</>
              : <>Add it as another spelling of a {manufacturerName} tier</>}
            {synonymAllowed && (
              <select value={targetId} onChange={(e) => setTargetId(e.target.value)} className="ml-2 border border-gray-300 rounded px-1 py-0.5 text-xs" disabled={choice !== 'synonym'}>
                {grades.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.canonicalName}
                  </option>
                ))}
              </select>
            )}
          </span>
        </label>
        <label className="flex gap-2">
          <input type="radio" name="promote" checked={choice === 'new'} onChange={() => setChoice('new')} className="mt-1" />
          <span>Add it as a new {manufacturerName} tier{entry.brandId ? ' (a brand tier)' : ''}, with its spellings, rank and description</span>
        </label>
        {source === 'add' && (
          <label className="flex gap-2">
            <input type="radio" name="promote" checked={choice === 'family'} onChange={() => setChoice('family')} className="mt-1" />
            <span>Keep it for this family only</span>
          </label>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={busy}
            disabled={busy || (choice === 'synonym' && !targetId)}
            onClick={async () => {
              setBusy(true);
              try {
                await onChoose(choice, targetId);
              } finally {
                setBusy(false);
              }
            }}
          >
            {choice === 'family' ? 'Add to this family' : 'Promote'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function RemoveCopiesDialog({
  state,
  onClose,
  onRemove,
}: {
  state: CopiesState;
  onClose: () => void;
  onRemove: (ticked: CopiesState['rows']) => Promise<void>;
}) {
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  return (
    <Modal isOpen onClose={onClose} title="Remove copies from other families?" size="md">
      <div className="space-y-3 text-sm">
        <p>
          &ldquo;{state.name}&rdquo; is now on the manufacturer. These families still list it themselves — tick the ones
          to remove it from.
        </p>
        <ul className="space-y-1">
          {state.rows.map((r) => (
            <li key={r.family.id}>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={ticked.has(r.family.id)}
                  onChange={() =>
                    setTicked((prev) => {
                      const next = new Set(prev);
                      if (next.has(r.family.id)) next.delete(r.family.id);
                      else next.add(r.family.id);
                      return next;
                    })
                  }
                />
                {r.family.name} family
                <span className="text-xs text-gray-500">({r.copies.map((c) => c.canonicalName).join(', ')})</span>
              </label>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Keep them
          </Button>
          <Button
            size="sm"
            loading={busy}
            disabled={busy || ticked.size === 0}
            onClick={async () => {
              setBusy(true);
              try {
                await onRemove(state.rows.filter((r) => ticked.has(r.family.id)));
              } finally {
                setBusy(false);
              }
            }}
          >
            Remove from {ticked.size}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
